"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const jsx_runtime_1 = require("react/jsx-runtime");
const react_1 = require("react");
const react_modal_1 = __importDefault(require("react-modal"));
const styled_components_1 = __importDefault(require("styled-components"));
const lodash_1 = require("lodash");
const theme_1 = require("../../../theme");
const DocAdminPoolSection_1 = __importDefault(require("./DocAdminPoolSection"));
const TransactionAttachments_1 = require("../common/TransactionAttachments");
const useFetch_1 = __importDefault(require("../../../hooks/useFetch"));
const useSendFile_1 = __importDefault(require("../../../hooks/useSendFile"));
const useEdit_1 = __importDefault(require("../../../hooks/useEdit"));
const timeUtils_1 = require("../../../util/timeUtils");
const YearContext_1 = require("../../../contexts/YearContext");
const apiUtils_1 = require("../../../util/apiUtils");
const common_styled_components_1 = require("../../common-styled-components");
const isNullish_1 = require("../../../util/isNullish");
const TextInput_1 = require("../../generic/TextInput");
// ─── Styled components ──────────────────────────────────────────────────────
const DropZone = styled_components_1.default.div `
  border: 2px dashed
    ${({ $isDragging }) => $isDragging ? theme_1.theme.colors.blue : theme_1.theme.colors.grey(3)};
  border-radius: 8px;
  padding: ${theme_1.theme.spacing(4)};
  text-align: center;
  color: ${({ $isDragging }) => $isDragging ? theme_1.theme.colors.blue : theme_1.theme.colors.grey(2)};
  background-color: ${({ $isDragging }) => $isDragging ? theme_1.theme.colors.grey(5) : "transparent"};
  cursor: pointer;
  margin-bottom: ${theme_1.theme.spacing(3)};
  transition: border-color 0.2s, background-color 0.2s, color 0.2s;
`;
const DropZoneText = styled_components_1.default.span `
  font-size: 0.875rem;
`;
const ModalContent = styled_components_1.default.div `
  display: flex;
  flex-direction: column;
  gap: ${theme_1.theme.spacing(3)};
  width: 100%;
`;
const Form = styled_components_1.default.form `
  display: flex;
  flex-direction: column;
  gap: ${theme_1.theme.spacing(3)};
`;
const FileNameContainer = styled_components_1.default.div `
  display: flex;
  flex-direction: column;
  gap: ${theme_1.theme.spacing(1)};
  padding: ${theme_1.theme.spacing(2)};
  background-color: ${theme_1.theme.colors.grey(5)};
  border-radius: 4px;
`;
const FileNameLabel = styled_components_1.default.span `
  font-size: 0.875rem;
  color: ${theme_1.theme.colors.grey(2)};
  font-weight: 500;
`;
const FileName = styled_components_1.default.span `
  font-family: monospace;
  word-break: break-all;
`;
const ToolsRow = styled_components_1.default.div `
  display: flex;
  gap: ${theme_1.theme.spacing(1)};
`;
const SearchContainer = styled_components_1.default.div `
  margin-bottom: ${theme_1.theme.spacing(2)};
`;
const SearchRow = styled_components_1.default.div `
  display: flex;
  gap: ${theme_1.theme.spacing(1)};
  align-items: center;
`;
const SearchHelperText = styled_components_1.default.p `
  font-size: 0.75rem;
  color: ${theme_1.theme.colors.grey(2)};
  margin: ${theme_1.theme.spacing(1)} 0 0;
`;
const modalStyles = {
    ...common_styled_components_1.baseReactModalStyles,
    content: {
        ...common_styled_components_1.baseReactModalStyles.content,
        minWidth: "480px",
        display: "flex",
    },
};
const getDateDaysAgo = (daysAgo) => {
    const date = new Date();
    date.setDate(date.getDate() - daysAgo);
    return (0, timeUtils_1.formatToDateInputValue)(date);
};
const getFileNameWithoutExtension = (fileName) => {
    const extensionIndex = fileName.lastIndexOf(".");
    return extensionIndex > 0 ? fileName.slice(0, extensionIndex) : fileName;
};
const maxPOSFileSize = 30 * 1024 * 1024 - 500;
const partitionPOSFiles = (files) => ({
    acceptedFiles: files.filter((file) => file.size <= maxPOSFileSize),
    rejectedFiles: files.filter((file) => file.size > maxPOSFileSize),
});
const getInitialPOSFormData = (selectedYear, file) => {
    const currentYear = new Date().getFullYear();
    const defaultDate = selectedYear < currentYear ? new Date(selectedYear, 11, 1) : new Date();
    return {
        year: selectedYear,
        date: (0, timeUtils_1.formatToDateInputValue)(defaultDate),
        document_id: file ? getFileNameWithoutExtension(file.name) : undefined,
    };
};
const POSUploader = ({ selectedYear, initialFiles = [], isOpen, onClose, refresh, }) => {
    const [queuedFiles, setQueuedFiles] = (0, react_1.useState)([]);
    const [processedCount, setProcessedCount] = (0, react_1.useState)(0);
    const [submitting, setSubmitting] = (0, react_1.useState)(false);
    const [errorText, setErrorText] = (0, react_1.useState)("");
    const [formData, setFormData] = (0, react_1.useState)(getInitialPOSFormData(selectedYear));
    const [sendFile, fileSendMessage, resetSendStatus] = (0, useSendFile_1.default)("admin/add_proof_of_sustainability");
    const activeFile = queuedFiles[0] ?? null;
    const totalFilesCount = processedCount + queuedFiles.length;
    (0, react_1.useEffect)(() => {
        if (isOpen) {
            setQueuedFiles(initialFiles);
            setProcessedCount(0);
            setErrorText("");
        }
    }, [initialFiles, isOpen]);
    (0, react_1.useEffect)(() => {
        setFormData(getInitialPOSFormData(selectedYear, activeFile));
        resetSendStatus();
    }, [activeFile, resetSendStatus, selectedYear]);
    // Reset when year changes
    (0, react_1.useEffect)(() => {
        setFormData((prev) => ({ ...prev, year: selectedYear }));
    }, [selectedYear]);
    const updateValue = (0, react_1.useCallback)((key, value) => {
        setFormData((prev) => ({ ...prev, [key]: value }));
    }, []);
    const handleFileChange = (0, react_1.useCallback)((selectedFile) => {
        if (selectedFile === null) {
            setErrorText("File size exceeds 30MB limit.");
            setQueuedFiles([]);
            return;
        }
        setErrorText("");
        setQueuedFiles([selectedFile]);
        setProcessedCount(0);
    }, []);
    const handleClose = (0, react_1.useCallback)(() => {
        setQueuedFiles([]);
        setProcessedCount(0);
        setSubmitting(false);
        setErrorText("");
        resetSendStatus();
        setFormData(getInitialPOSFormData(selectedYear));
        onClose();
    }, [onClose, resetSendStatus, selectedYear]);
    const moveToNextQueuedFile = (0, react_1.useCallback)((shouldRefresh) => {
        const remainingFiles = queuedFiles.slice(1);
        if (shouldRefresh) {
            refresh?.();
        }
        if (remainingFiles.length === 0) {
            handleClose();
            return;
        }
        setProcessedCount((prev) => prev + 1);
        setQueuedFiles(remainingFiles);
    }, [handleClose, queuedFiles, refresh]);
    const handleRemoveCurrentFile = (0, react_1.useCallback)(() => {
        if (!activeFile) {
            return;
        }
        moveToNextQueuedFile(false);
    }, [activeFile, moveToNextQueuedFile]);
    const handleSubmit = async (event) => {
        event.preventDefault();
        if (!activeFile)
            return;
        const data = new FormData();
        data.append("attachment", activeFile);
        Object.entries(formData).forEach(([key, value]) => {
            if (!(0, isNullish_1.isNullish)(value)) {
                data.append(key, value.toString());
            }
        });
        setSubmitting(true);
        const uploadSucceeded = await sendFile(data);
        setSubmitting(false);
        if (uploadSucceeded) {
            moveToNextQueuedFile(true);
        }
    };
    const disabled = activeFile === null;
    return ((0, jsx_runtime_1.jsx)(react_modal_1.default, { isOpen: isOpen, onRequestClose: handleClose, contentLabel: "Add Proof of Sustainability", style: modalStyles, shouldCloseOnEsc: true, children: (0, jsx_runtime_1.jsxs)(ModalContent, { children: [(0, jsx_runtime_1.jsx)("h2", { children: "Add Proof of Sustainability" }), (0, jsx_runtime_1.jsxs)(Form, { onSubmit: handleSubmit, children: [!activeFile && ((0, jsx_runtime_1.jsx)(TransactionAttachments_1.FileUploadInput, { onFileChange: handleFileChange, error: errorText })), activeFile && ((0, jsx_runtime_1.jsxs)(FileNameContainer, { children: [(0, jsx_runtime_1.jsxs)(FileNameLabel, { children: ["Current file (", processedCount + 1, " / ", totalFilesCount, ")"] }), (0, jsx_runtime_1.jsx)(FileName, { children: activeFile.name }), queuedFiles.length > 1 && ((0, jsx_runtime_1.jsxs)(common_styled_components_1.P, { children: [queuedFiles.length - 1, " more file(s) queued after this one."] }))] })), !activeFile && ((0, jsx_runtime_1.jsx)(common_styled_components_1.P, { children: "Select one or more POS note files to begin review." })), activeFile && ((0, jsx_runtime_1.jsx)(TransactionAttachments_1.ProofOfSustainabilityForm, { state: formData, updateValue: updateValue })), (0, jsx_runtime_1.jsx)(common_styled_components_1.Button, { type: "submit", disabled: disabled || submitting, children: disabled
                                ? "Select files to review"
                                : submitting
                                    ? "Submitting file"
                                    : queuedFiles.length > 1
                                        ? "Save and continue"
                                        : "Upload" })] }), fileSendMessage && (0, jsx_runtime_1.jsx)("p", { children: fileSendMessage }), (0, jsx_runtime_1.jsxs)(ToolsRow, { children: [(0, jsx_runtime_1.jsx)(common_styled_components_1.SecondaryButton, { type: "button", onClick: handleRemoveCurrentFile, disabled: !activeFile || submitting, children: queuedFiles.length > 1 ? "Skip file" : "Remove file" }), (0, jsx_runtime_1.jsx)(common_styled_components_1.SecondaryButton, { type: "button", onClick: handleClose, children: "Close" })] })] }) }));
};
const POSEditor = ({ posNote, isOpen, onClose, refresh, }) => {
    const [editableData, setEditableData] = (0, react_1.useState)(posNote);
    const [editStatus, executeEdit] = (0, useEdit_1.default)("admin/proof_of_sustainability.json", refresh);
    // Keep in sync when the underlying record changes
    (0, react_1.useEffect)(() => {
        setEditableData(posNote);
    }, [posNote]);
    (0, react_1.useEffect)(() => {
        if (editStatus.status === apiUtils_1.LoaderStatus.Success) {
            onClose();
        }
    }, [editStatus.status, onClose]);
    const updateValue = (0, react_1.useCallback)((key, value) => {
        setEditableData((prev) => ({ ...prev, [key]: value }));
    }, []);
    const handleSubmit = (event) => {
        event.preventDefault();
        const data = (0, lodash_1.pickBy)(editableData, (_value, key) => key !== "name");
        executeEdit(data, "PUT");
    };
    return ((0, jsx_runtime_1.jsx)(react_modal_1.default, { isOpen: isOpen, onRequestClose: onClose, contentLabel: "Edit Proof of Sustainability", style: modalStyles, shouldCloseOnEsc: true, children: (0, jsx_runtime_1.jsxs)(ModalContent, { children: [(0, jsx_runtime_1.jsx)("h2", { children: "Edit Proof of Sustainability" }), (0, jsx_runtime_1.jsxs)(FileNameContainer, { children: [(0, jsx_runtime_1.jsx)(FileNameLabel, { children: "File:" }), (0, jsx_runtime_1.jsx)(FileName, { children: posNote.name })] }), (0, jsx_runtime_1.jsxs)(Form, { onSubmit: handleSubmit, children: [(0, jsx_runtime_1.jsx)(TransactionAttachments_1.ProofOfSustainabilityForm, { state: editableData, updateValue: updateValue }), (0, jsx_runtime_1.jsx)(common_styled_components_1.Button, { type: "submit", disabled: editStatus.status !== apiUtils_1.LoaderStatus.Idle, children: editStatus.status === apiUtils_1.LoaderStatus.Idle
                                ? "Save Changes"
                                : "Saving changes..." })] }), (0, jsx_runtime_1.jsx)(common_styled_components_1.SecondaryButton, { type: "button", onClick: onClose, children: "Close" })] }) }));
};
const POSTable = ({ posNotes, refresh }) => {
    const [editingNote, setEditingNote] = (0, react_1.useState)(null);
    const [pendingDelete, setPendingDelete] = (0, react_1.useState)(null);
    const [_deleteStatus, executeDelete] = (0, useEdit_1.default)("admin/proof_of_sustainability.json", refresh);
    const confirmDelete = () => {
        if (!(0, isNullish_1.isNullish)(pendingDelete)) {
            executeDelete({ id: pendingDelete.id }, "DELETE");
            setPendingDelete(null);
        }
    };
    if (posNotes.length === 0) {
        return (0, jsx_runtime_1.jsx)(common_styled_components_1.P, { children: "No Proof of Sustainability files uploaded" });
    }
    return ((0, jsx_runtime_1.jsxs)(jsx_runtime_1.Fragment, { children: [editingNote && ((0, jsx_runtime_1.jsx)(POSEditor, { posNote: editingNote, isOpen: true, onClose: () => setEditingNote(null), refresh: refresh })), (0, jsx_runtime_1.jsx)(react_modal_1.default, { isOpen: !(0, isNullish_1.isNullish)(pendingDelete), onRequestClose: () => setPendingDelete(null), contentLabel: "Confirm delete", style: modalStyles, shouldCloseOnEsc: true, children: (0, jsx_runtime_1.jsxs)(ModalContent, { children: [(0, jsx_runtime_1.jsx)("h2", { children: "Remove Proof of Sustainability" }), (0, jsx_runtime_1.jsxs)("p", { children: ["Are you sure you want to remove", " ", (0, jsx_runtime_1.jsx)("strong", { children: pendingDelete?.name }), "?"] }), (0, jsx_runtime_1.jsxs)(ToolsRow, { children: [(0, jsx_runtime_1.jsx)(common_styled_components_1.Button, { type: "button", onClick: confirmDelete, children: "Remove" }), (0, jsx_runtime_1.jsx)(common_styled_components_1.SecondaryButton, { type: "button", onClick: () => setPendingDelete(null), children: "Cancel" })] })] }) }), (0, jsx_runtime_1.jsxs)(common_styled_components_1.Table, { children: [(0, jsx_runtime_1.jsx)("thead", { children: (0, jsx_runtime_1.jsxs)("tr", { children: [(0, jsx_runtime_1.jsx)(common_styled_components_1.TH, { children: "File" }), (0, jsx_runtime_1.jsx)(common_styled_components_1.TH, { children: "Date" }), (0, jsx_runtime_1.jsx)(common_styled_components_1.TH, { children: "Document ID" }), (0, jsx_runtime_1.jsx)(common_styled_components_1.TH, { children: "Tools" })] }) }), (0, jsx_runtime_1.jsx)("tbody", { children: posNotes.map((note) => ((0, jsx_runtime_1.jsxs)(common_styled_components_1.TR, { children: [(0, jsx_runtime_1.jsx)(common_styled_components_1.TD, { children: note.name }), (0, jsx_runtime_1.jsx)(common_styled_components_1.TD, { children: (() => {
                                        const d = new Date(note.date);
                                        return isNaN(d.getTime()) ? note.date : (0, timeUtils_1.formatDate)(d, true);
                                    })() }), (0, jsx_runtime_1.jsx)(common_styled_components_1.TD, { children: note.document_id }), (0, jsx_runtime_1.jsx)(common_styled_components_1.TD, { children: (0, jsx_runtime_1.jsxs)(ToolsRow, { children: [(0, jsx_runtime_1.jsx)(common_styled_components_1.SmallButton, { onClick: () => setEditingNote(note), children: "Edit" }), (0, jsx_runtime_1.jsx)(common_styled_components_1.SmallButton, { onClick: () => setPendingDelete(note), children: "Remove" })] }) })] }, note.id))) })] })] }));
};
// ─── Page ────────────────────────────────────────────────────────────────────
function AdminDocuments() {
    const { selectedYear } = (0, YearContext_1.useYear)();
    const [isDragging, setIsDragging] = (0, react_1.useState)(false);
    const [isUploaderOpen, setIsUploaderOpen] = (0, react_1.useState)(false);
    const [queuedUploadFiles, setQueuedUploadFiles] = (0, react_1.useState)([]);
    const [selectionError, setSelectionError] = (0, react_1.useState)(null);
    const [query, setQuery] = (0, react_1.useState)("");
    const [dateFrom, setDateFrom] = (0, react_1.useState)(() => getDateDaysAgo(30));
    const [displayNotes, setDisplayNotes] = (0, react_1.useState)(undefined);
    const [searchError, setSearchError] = (0, react_1.useState)(null);
    const [posNotes, _setPosUrl, setPosBody, refreshPosNotes] = (0, useFetch_1.default)("admin/proof_of_sustainability_search.json", undefined, true);
    const canSearch = query.trim().length >= 3 || !(0, isNullish_1.isNullish)(dateFrom);
    (0, react_1.useEffect)(() => {
        const trimmed = query.trim();
        if (trimmed.length < 3 && (0, isNullish_1.isNullish)(dateFrom)) {
            setDisplayNotes(undefined);
            setSearchError(null);
            return;
        }
        const timeout = setTimeout(() => {
            setSearchError(null);
            const payload = (0, lodash_1.pickBy)({
                search: trimmed.length >= 3 ? trimmed : undefined,
                date_from: dateFrom,
            });
            setPosBody(JSON.stringify(payload));
        }, 300);
        return () => clearTimeout(timeout);
    }, [dateFrom, query, selectedYear, setPosBody]);
    (0, react_1.useEffect)(() => {
        if (posNotes.status === apiUtils_1.LoaderStatus.Success && posNotes.data) {
            setSearchError(null);
            setDisplayNotes(posNotes.data);
        }
        else if (posNotes.status === apiUtils_1.LoaderStatus.Error) {
            setSearchError("Search failed; please try again.");
            setDisplayNotes(undefined);
        }
    }, [posNotes]);
    const refresh = (0, react_1.useCallback)(() => {
        if (query.trim().length >= 3 || !(0, isNullish_1.isNullish)(dateFrom)) {
            refreshPosNotes();
        }
    }, [dateFrom, query, refreshPosNotes]);
    const handleSearchChange = (0, react_1.useCallback)((value) => {
        setQuery(value);
        setDateFrom(undefined);
    }, []);
    const handleLatest30DaysSearch = (0, react_1.useCallback)(() => {
        setQuery("");
        setDateFrom(getDateDaysAgo(30));
    }, []);
    const openUploader = (files = []) => {
        setQueuedUploadFiles(files);
        setIsUploaderOpen(true);
    };
    const handleSelectedFiles = (0, react_1.useCallback)((files) => {
        const { acceptedFiles, rejectedFiles } = partitionPOSFiles(files);
        if (rejectedFiles.length > 0) {
            const rejectedNames = rejectedFiles.map((file) => file.name).join(", ");
            setSelectionError(`Skipped ${rejectedFiles.length} oversized file${rejectedFiles.length === 1 ? "" : "s"}: ${rejectedNames}`);
        }
        else {
            setSelectionError(null);
        }
        if (acceptedFiles.length > 0) {
            openUploader(acceptedFiles);
        }
    }, []);
    const handleDragOver = (0, react_1.useCallback)((e) => {
        e.preventDefault();
        setIsDragging(true);
    }, []);
    const handleDragLeave = (0, react_1.useCallback)(() => {
        setIsDragging(false);
    }, []);
    const handleDrop = (0, react_1.useCallback)((e) => {
        e.preventDefault();
        setIsDragging(false);
        const files = Array.from(e.dataTransfer.files ?? []);
        if (files.length > 0) {
            handleSelectedFiles(files);
        }
    }, [handleSelectedFiles]);
    return ((0, jsx_runtime_1.jsxs)(common_styled_components_1.PageContent, { align: "left", children: [(0, jsx_runtime_1.jsx)(common_styled_components_1.ContentGrid, { "$cols": 1, children: (0, jsx_runtime_1.jsxs)(common_styled_components_1.GridCard, { children: [(0, jsx_runtime_1.jsx)(common_styled_components_1.SmallSubtitle, { children: "Proof of Sustainability Notes" }), (0, jsx_runtime_1.jsx)(DropZone, { "$isDragging": isDragging, onDragOver: handleDragOver, onDragLeave: handleDragLeave, onDrop: handleDrop, onClick: () => openUploader(), children: (0, jsx_runtime_1.jsx)(DropZoneText, { children: isDragging
                                    ? "Drop files to add POS notes"
                                    : "Drop files here or click to add Proof of Sustainability notes" }) }), selectionError && ((0, jsx_runtime_1.jsx)(SearchHelperText, { style: { color: theme_1.theme.colors.red }, children: selectionError })), (0, jsx_runtime_1.jsx)(POSUploader, { selectedYear: selectedYear, initialFiles: queuedUploadFiles, isOpen: isUploaderOpen, onClose: () => {
                                setIsUploaderOpen(false);
                                setQueuedUploadFiles([]);
                            }, refresh: refresh }), (0, jsx_runtime_1.jsxs)(SearchContainer, { children: [(0, jsx_runtime_1.jsxs)(SearchRow, { children: [(0, jsx_runtime_1.jsx)(TextInput_1.TextInput, { value: query, onChange: handleSearchChange, placeholder: "Search POS notes\u2026" }), (0, jsx_runtime_1.jsx)(common_styled_components_1.SmallButton, { type: "button", onClick: handleLatest30DaysSearch, children: "Latest 30 days" })] }), !canSearch && ((0, jsx_runtime_1.jsx)(SearchHelperText, { children: "Enter at least 3 characters to search." })), dateFrom && ((0, jsx_runtime_1.jsxs)(SearchHelperText, { children: ["Showing POS notes from ", dateFrom, " onward. Search above to find older notes."] })), searchError && ((0, jsx_runtime_1.jsx)(SearchHelperText, { style: { color: theme_1.theme.colors.red }, children: searchError }))] }), canSearch && posNotes.status === apiUtils_1.LoaderStatus.Loading && ((0, jsx_runtime_1.jsx)(common_styled_components_1.P, { children: "Searching\u2026" })), canSearch && posNotes.status !== apiUtils_1.LoaderStatus.Loading && ((0, jsx_runtime_1.jsx)(POSTable, { posNotes: displayNotes ?? [], refresh: refresh }))] }) }), (0, jsx_runtime_1.jsx)(common_styled_components_1.ContentGrid, { "$cols": 1, children: (0, jsx_runtime_1.jsxs)(common_styled_components_1.GridCard, { style: { minHeight: "400px" }, children: [(0, jsx_runtime_1.jsx)(common_styled_components_1.SmallSubtitle, { children: "Pool Documents" }), (0, jsx_runtime_1.jsx)(DocAdminPoolSection_1.default, { selectedYear: selectedYear })] }) })] }));
}
exports.default = AdminDocuments;
